import React, { useState, useEffect } from 'react';
import { getUserTimezone, getTimezoneAbbreviation } from '../utils/timezone.utils';

/**
 * Clock component that displays current time in user's selected timezone
 * Updates every second and responds to timezone changes
 */
const TimezoneClock = ({ timezone }) => {
    const [currentTime, setCurrentTime] = useState(new Date());
    const [displayTimezone, setDisplayTimezone] = useState(timezone || getUserTimezone());

    // Listen to timezone changes
    useEffect(() => {
        const handler = (e) => {
            if (e.detail?.timezone) {
                setDisplayTimezone(e.detail.timezone);
            } else if (!timezone) {
                // If no prop provided, read from localStorage
                setDisplayTimezone(getUserTimezone());
            }
        };
        
        window.addEventListener('tb:timezoneChanged', handler);
        
        return () => {
            window.removeEventListener('tb:timezoneChanged', handler);
        };
    }, [timezone]);

    // Update display timezone when prop changes
    useEffect(() => {
        if (timezone) {
            setDisplayTimezone(timezone);
        }
    }, [timezone]);

    useEffect(() => {
        // Update clock every second
        const interval = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    // Format the time with timezone abbreviation using the display timezone
    let formattedTimeWithTz;
    try {
        const date = currentTime;
        const userTz = displayTimezone;
        
        const formatOptions = {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
            timeZone: userTz
        };
        
        const formatted = new Intl.DateTimeFormat('en-US', formatOptions).format(date);
        const tzAbbr = getTimezoneAbbreviation(userTz, date);
        formattedTimeWithTz = `${formatted} ${tzAbbr}`;
    } catch (error) {
        console.error('Error formatting clock time:', error);
        formattedTimeWithTz = '--:--:-- UTC';
    }

    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 12px',
            backgroundColor: '#f8f9fa',
            borderRadius: '6px',
            border: '1px solid #e9ecef',
            fontFamily: 'monospace',
            fontSize: '0.9em',
            fontWeight: '600',
            color: '#374151',
            minWidth: '140px',
            justifyContent: 'center'
        }}>
            <span style={{ fontSize: '1.1em' }}>🕐</span>
            <span>{formattedTimeWithTz}</span>
        </div>
    );
};

export default TimezoneClock;
